package dev.akshaychordiya.components.layouts

import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import dev.akshaychordiya.BODY_FONT
import dev.akshaychordiya.DISPLAY_FONT
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.*

/**
 * Typography for rendered Markdown.
 *
 * Kept as CSS rules rather than Compose components because the Markdown plugin emits plain HTML tags - this is the
 * only place that can style them.
 */
val ProseStyle = CssStyle {
    base { Modifier.fillMaxWidth().lineHeight(1.7) }

    // The display face applies to every heading level inside an article.
    cssRule("h1, h2, h3, h4") {
        Modifier.fontFamily(DISPLAY_FONT, BODY_FONT, "-apple-system", "sans-serif")
    }

    cssRule("h1") {
        Modifier
            .fontSize(2.3.cssRem)
            .fontWeight(700)
            .letterSpacing((-0.04).cssRem)
            .lineHeight(1.15)
            .margin(top = 0.px, bottom = 1.cssRem)
    }

    cssRule("h2") {
        Modifier
            .fontSize(1.6.cssRem)
            .fontWeight(650)
            .letterSpacing((-0.025).cssRem)
            .lineHeight(1.25)
            .margin(top = 2.5.cssRem, bottom = 0.75.cssRem)
    }

    cssRule("h3") {
        Modifier
            .fontSize(1.25.cssRem)
            .fontWeight(600)
            .lineHeight(1.3)
            .margin(top = 2.cssRem, bottom = 0.5.cssRem)
    }

    cssRule("h4") {
        Modifier.fontSize(1.05.cssRem).fontWeight(FontWeight.Bolder).margin(top = 1.5.cssRem, bottom = 0.4.cssRem)
    }

    cssRule("p") {
        Modifier.margin(topBottom = 1.1.cssRem).overflowWrap(OverflowWrap.BreakWord)
    }

    cssRule("a") {
        Modifier
            .color(colorMode.toSitePalette().brandPrimary)
            .textDecorationLine(TextDecorationLine.Underline)
    }

    cssRule("ul, ol") {
        Modifier.margin(topBottom = 1.1.cssRem).padding(left = 1.4.cssRem)
    }

    cssRule("li") {
        Modifier.margin(bottom = 0.4.cssRem)
    }

    // Inline code. `pre > code` below resets what it needs to for blocks.
    cssRule("code") {
        Modifier
            .fontSize(0.9.em)
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(0.3.cssRem)
            .padding(leftRight = 0.3.cssRem, topBottom = 0.1.cssRem)
    }

    // Callouts, emitted by the `blockquote` handler in build.gradle.kts when a quote opens with [!NOTE] and
    // friends. Tinted from the palette rather than fixed colours so both themes follow automatically; the left border
    // carries the meaning, so these still read correctly if the tint is lost to forced-colours mode.
    cssRule(".callout") {
        Modifier
            .margin(topBottom = 1.5.cssRem)
            .padding(topBottom = 0.9.cssRem, leftRight = 1.1.cssRem)
            .borderRadius(0.5.cssRem)
            .backgroundColor(colorMode.toSitePalette().surface)
            .styleModifier { property("border-left", "3px solid ${colorMode.toSitePalette().subdued}") }
    }
    // One accent per kind. Hues are fixed rather than palette-derived because their meaning is conventional - amber
    // warns, red stops - but each has a light and dark variant so neither theme gets a muddy or glaring border.
    cssRule(".callout-note") {
        Modifier.styleModifier {
            property("border-left-color", if (colorMode.isLight) "#1F7A4D" else "#4ED88F")
        }
    }
    cssRule(".callout-tip") {
        Modifier.styleModifier {
            property("border-left-color", if (colorMode.isLight) "#0550AE" else "#79C0FF")
        }
    }
    cssRule(".callout-important") {
        Modifier.styleModifier {
            property("border-left-color", if (colorMode.isLight) "#8250DF" else "#D2A8FF")
        }
    }
    cssRule(".callout-warning") {
        Modifier.styleModifier {
            property("border-left-color", if (colorMode.isLight) "#9A6700" else "#E8A85F")
        }
    }
    cssRule(".callout-caution") {
        Modifier.styleModifier {
            property("border-left-color", if (colorMode.isLight) "#CF222E" else "#FF7B72")
        }
    }

    cssRule(".callout > :first-child") { Modifier.margin(top = 0.px) }
    cssRule(".callout > :last-child") { Modifier.margin(bottom = 0.px) }

    cssRule("pre") {
        Modifier
            .margin(topBottom = 1.5.cssRem)
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(0.6.cssRem)
            .padding(1.cssRem)
            .overflow { x(Overflow.Auto) }
    }

    // Inside a block, the code element is just text - the <pre> owns the frame.
    cssRule("pre > code") {
        Modifier
            .fontSize(0.85.cssRem)
            .lineHeight(1.6)
            .backgroundColor(Colors.Transparent)
            .border { style(LineStyle.None) }
            .borderRadius(0.px)
            .padding(0.px)
    }

    cssRule("blockquote") {
        Modifier
            .margin(topBottom = 1.5.cssRem, leftRight = 0.px)
            .padding(left = 1.2.cssRem)
            .borderLeft(3.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary)
            .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
            .fontStyle(FontStyle.Italic)
    }

    cssRule("img") {
        Modifier
            .maxWidth(100.percent)
            .borderRadius(0.6.cssRem)
            .margin(topBottom = 1.5.cssRem)
            // Keep the intrinsic aspect ratio as the image is scaled down to fit the column.
            .styleModifier { property("height", "auto") }
    }

    cssRule("hr") {
        Modifier
            .margin(topBottom = 2.5.cssRem)
            .border { style(LineStyle.None) }
            .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
    }

    // Wide tables scroll inside their own frame rather than forcing the whole page sideways.
    cssRule("table") {
        Modifier
            .fillMaxWidth()
            .margin(topBottom = 1.5.cssRem)
            .display(DisplayStyle.Block)
            .overflow { x(Overflow.Auto) }
            .styleModifier { property("border-collapse", "collapse") }
    }

    cssRule("th, td") {
        Modifier
            .padding(topBottom = 0.5.cssRem, leftRight = 0.75.cssRem)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .textAlign(TextAlign.Start)
    }
}
